import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../features/auth/contexts/AuthContext";
import { AuthPage } from "../features/auth/components/AuthComponents";
import { CustomSignupPage } from "../features/auth/components/CustomSignupPage";
import { PasswordSetupPage } from "../features/auth/components/PasswordSetupPage";
import { MainLayout } from "../shared/components/layout/MainLayout";
import { AppLayout } from "../shared/components/layout/AppLayout";
import { ProjectTabLayout } from "../shared/components/layout/ProjectTabLayout";
import { WelcomePage } from "../features/projects/pages/WelcomePage";
import { Homepage } from "../features/projects/pages/Homepage";
import { SettingsPage } from "../features/auth/pages/SettingsPage";
import { TeamSettingsPage } from "../features/teams/pages/TeamSettingsPage";
import { TeamInvitationsPage } from "../features/teams/pages/TeamInvitationsPage";
import { InvitationResponsePage } from "../features/auth/pages/InvitationResponsePage";
import { CalendarPage } from "../shared/components/ui/CalendarPage";
import MoleculeViewerPage from "../features/molecules/pages/MoleculeViewerPage"; // ADD THIS IMPORT
import { OdysseyJobDetails } from "../features/jobs/components/OdysseyJobDetails";

export default function App() {
  const { user, loading } = useAuth();
  
  // Check URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const currentPath = window.location.pathname;
  
  // Check for different signup scenarios
  const isInviteSignup = urlParams.get('type') === 'invite' && urlParams.get('token');
  const isSignupWithToken = currentPath === '/signup' && urlParams.get('token');
  const isPasswordSetup = urlParams.get('type') === 'password_setup';

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show custom signup page for invitation signups (both URL patterns)
  if ((isInviteSignup || isSignupWithToken) && !user) {
    return <CustomSignupPage />;
  }

  // Show password setup page if it's a password setup flow
  if (isPasswordSetup) {
    return <PasswordSetupPage />;
  }

  // Show auth page if not authenticated
  if (!user) {
    return <AuthPage />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* ROUTES WITH TOP BAR */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/projects/:projectId/*" element={<ProjectTabLayout />} />
        </Route>
        
        {/* STANDALONE PAGES - No layout */}
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/team-settings/:teamId" element={<TeamSettingsPage />} />
        <Route path="/team-invitations" element={<TeamInvitationsPage />} />
        
        {/* ADD THIS ROUTE FOR MOLECULE VIEWER */}
        <Route path="/viewer/:id" element={<MoleculeViewerPage />} />
        
        {/* ODYSSEY JOB DETAILS ROUTE */}
        <Route path="/odyssey/jobs/:jobId" element={<OdysseyJobDetails />} />
        
        {/* ✅ NEW: Add explicit signup route */}
        <Route path="/signup" element={<CustomSignupPage />} />
        
        {/* MAIN LAYOUT ROUTES - These use MainLayout */}
        <Route path="/home" element={<MainLayout />}>
          <Route index element={<Homepage />} />
        </Route>
        <Route path="/invitations/:token" element={<MainLayout />}>
          <Route index element={<InvitationResponsePage />} />
        </Route>
        
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}